/**
 * Утилиты для оптимизации производительности
 */

/**
 * Debounce функция - откладывает выполнение до прекращения вызовов
 * 
 * @param {Function} fn Функция для выполнения
 * @param {number} delay Задержка в миллисекундах
 * @returns {Function} Debounced функция
 */
export function debounce(fn, delay = 300) {
  let timeoutId = null
  
  const debouncedFn = function(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    
    timeoutId = setTimeout(() => {
      fn.apply(this, args)
      timeoutId = null
    }, delay)
  }
  
  // Метод для отмены ожидающего вызова
  debouncedFn.cancel = function() {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }
  
  // Метод для немедленного выполнения
  debouncedFn.flush = function(...args) {
    debouncedFn.cancel()
    fn.apply(this, args)
  }
  
  return debouncedFn
}

/**
 * Throttle функция - ограничивает частоту вызовов
 * 
 * @param {Function} fn Функция для выполнения
 * @param {number} limit Минимальный интервал между вызовами (мс)
 * @returns {Function} Throttled функция
 */
export function throttle(fn, limit = 100) {
  let lastCall = 0
  let timeoutId = null
  
  return function(...args) {
    const now = Date.now()
    const remaining = limit - (now - lastCall)
    
    if (remaining <= 0) {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      lastCall = now
      fn.apply(this, args)
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastCall = Date.now()
        timeoutId = null
        fn.apply(this, args)
      }, remaining)
    }
  }
}

/**
 * Composable для использования debounce в Vue компонентах
 * 
 * @param {Function} fn Функция для debounce
 * @param {number} delay Задержка
 * @returns {Object} { debouncedFn, cancel, flush }
 */
export function useDebouncedFn(fn, delay = 300) {
  const debouncedFn = debounce(fn, delay)
  
  return {
    debouncedFn,
    cancel: debouncedFn.cancel,
    flush: debouncedFn.flush
  }
}

/**
 * Создаёт debounced версию поискового запроса
 * Автоматически отменяет предыдущие запросы
 * 
 * @param {Function} searchFn Функция поиска (должна возвращать Promise)
 * @param {number} delay Задержка
 * @returns {Function} Debounced функция поиска
 */
export function createDebouncedSearch(searchFn, delay = 300) {
  let abortController = null
  
  const debouncedSearch = debounce(async function(query, ...args) {
    // Отменяем предыдущий запрос
    if (abortController) {
      abortController.abort()
    }
    
    // Создаём новый контроллер
    abortController = new AbortController()
    
    try {
      const result = await searchFn(query, { signal: abortController.signal }, ...args)
      return result
    } catch (error) {
      // Игнорируем ошибки отмены
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return null
      }
      throw error
    } finally {
      abortController = null
    }
  }, delay)
  
  // Добавляем метод отмены всех запросов
  debouncedSearch.cancelAll = function() {
    debouncedSearch.cancel()
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }
  
  return debouncedSearch
}

export default {
  debounce,
  throttle,
  useDebouncedFn,
  createDebouncedSearch
}
